import { useMemo } from 'react';
import { LTSSService, Alert, AlertLevel } from '../types/ltss';
import { generateAlerts } from '../utils/alertLogic';

interface AlertSummary {
  total: number;
  critical: number;
  warning: number;
  caution: number;
  info: number;
}

interface UseAlertsReturn {
  alerts: Alert[];
  summary: AlertSummary;
  getAlertsByType: (type: AlertLevel) => Alert[];
  getAlertsByCategory: (category: string) => Alert[];
}

export function useAlerts(services: LTSSService[]): UseAlertsReturn {
  const alerts = useMemo(() => generateAlerts(services), [services]);

  const summary = useMemo<AlertSummary>(() => {
    return alerts.reduce(
      (acc, alert) => {
        acc.total++;
        switch (alert.type) {
          case 'CRITICAL':
            acc.critical++;
            break;
          case 'WARNING':
            acc.warning++;
            break;
          case 'CAUTION':
            acc.caution++;
            break;
          case 'INFO':
            acc.info++;
            break;
        }
        return acc;
      },
      { total: 0, critical: 0, warning: 0, caution: 0, info: 0 }
    );
  }, [alerts]);

  const getAlertsByType = (type: AlertLevel): Alert[] => {
    return alerts.filter((alert) => alert.type === type);
  };

  const getAlertsByCategory = (category: string): Alert[] => {
    return alerts.filter((alert) => alert.category === category);
  };

  return {
    alerts,
    summary,
    getAlertsByType,
    getAlertsByCategory,
  };
}
